import * as localizationKeys from '../consts/localization/localizationKeys';
import * as tncTypes from '../consts/tnc/tncTypes';
import {hideLoader, setNotificationInfo, showLoader} from '../state/ducks/global';
import {setTncPopupCode} from '../state/ducks/tnc';
import {makeSelectAcceptedTncByType, makeSelectTncByType} from '../state/selectors/consumer';
import {selectAggregatedSettings} from '../state/selectors/consumerAggregatedSettings';
import {dispatch, getState} from '../state/store';
import helpers from '../utils/helpers';
import appErrorService from './app/appErrorService';
import * as gigyaService from './gigya/gigyaService';
import iccMarketService from './icc/iccMarketService';
import {getLocalizedStrings} from './localization/localizationService';
import networkStatusService from './networkStatusService';
import appRouterService from './route/appRouterService';

const acceptTnc = (tncList) => iccMarketService.setTnc({data: tncList});
const declineTnc = (tncList) => {
    const gigyaToken = gigyaService.getGigyaToken(true);

    return iccMarketService.declineTnc({data: tncList, gigyaToken});
};

const PATTERN_LINK = '@link';
const PATTERN_WEB_LINK = '@weblink';
const PATTERN_TNC_CODE = '@termsCode';
const PATTERN_CHANGE_LIST = '@changelist';
const PATTERN_TEXT = '@TEXT';
const ATTRIBUTE_DATA_TNC_CODE = 'data-tnc-code';

const getStringsBetweenPattern = (str, pattern) => str.match(new RegExp(pattern + '(.*?)' + pattern, 'ig'));
const getStringBetweenPattern = (str, pattern) => {
    const match = str.match(new RegExp(pattern + '(.*?)' + pattern, 'i'));

    if (match) {
        return {
            wholeText: match[0],
            capturedText: match[1],
        };
    }
    return {};
};

const getParsedDescription = (stringFull, className, legals, tncLegals) => {
    if (!stringFull) return '';

    const stringParsed = convertPatternsToString(stringFull, className, legals);

    return getChangeListDescription(stringParsed, className, legals, tncLegals);
};

const convertPatternsToString = (stringFull, className, legals) => {
    const getWebLinkStr = (str, wholeWebLink, capturedCode, capturedText) => {
        const legalData = getTncLegalByTermsCode(legals, capturedCode);

        if (legalData) {
            const testId = `term${capturedCode}Hyperlink`;
            const linkToReplace = `<a class="${className}" target="_blank" href="${legalData.webLink}" data-testid="${testId}">${capturedText}</a>`;

            return str.replace(wholeWebLink, linkToReplace);
        }
    };

    const getLinkStr = (str, wholeLink, capturedCode, capturedText) => {
        const testId = `term${capturedCode}Hyperlink`;
        const linkToReplace = `<span class="${className}" ${ATTRIBUTE_DATA_TNC_CODE}="${capturedCode}" role="link" tabindex="0" data-testid="${testId}">${capturedText}</span>`;

        return str.replace(wholeLink, linkToReplace);
    };

    let stringParsed = getParsedDescriptionPart(stringFull, PATTERN_WEB_LINK, getWebLinkStr);

    if (stringParsed) {
        stringParsed = getParsedDescriptionPart(stringParsed, PATTERN_LINK, getLinkStr);
    }

    return stringParsed;
};

const getChangeListDescription = (str, className, legals, tncLegals) => {
    const {wholeText} = getStringBetweenPattern(str, PATTERN_CHANGE_LIST);

    if (!wholeText) return str;

    const changeText = tncLegals.length
        ? tncLegals
              .sort((a, b) => a.position < b.position)
              .filter(({isInformational, changeList}) => !isInformational && changeList)
              .map(({changeList}) => changeList)
              .join('<br>')
        : '';
    const result = str.replace(wholeText, changeText);

    return convertPatternsToString(result, className, legals);
};

const getParsedDescriptionPart = (str, pattern, afterParseFunction) => {
    const matches = getStringsBetweenPattern(str, pattern);

    if (matches?.length) {
        matches.forEach((match) => {
            const capturedWebLink = match.replace(pattern, '');
            const {capturedText: capturedCode} = getStringBetweenPattern(capturedWebLink, PATTERN_TNC_CODE);
            const {capturedText} = getStringBetweenPattern(capturedWebLink, PATTERN_TEXT);

            str = afterParseFunction(str, match, capturedCode, capturedText);
        });
    }
    return str;
};

const getTncCodeFromTarget = (target, className) => {
    const tncLinkElement = target.closest(`.${className}`);

    return tncLinkElement?.getAttribute(ATTRIBUTE_DATA_TNC_CODE);
};

const getTncLegalByTermsCode = (legals, termsCode) => {
    if (!legals?.length) return null;

    legals.sort((a, b) => a.position < b.position);

    return termsCode ? legals.find((legal) => legal.code === termsCode) : legals[0];
};

const tncDataMapper = ({code, versionNumber}) => ({code, versionNumber});

const setPreferenceCenterSettings = async (legals, legalsSettingsNotNull, acceptedSettingList, onApproved) => {
    try {
        const legalsSettingsNull = legals.filter((l) => !l.settings && !l.isInformational).map(tncDataMapper);
        const legalsSettingsNotNullAccepted = legalsSettingsNotNull
            .filter((l) => acceptedSettingList.includes(l.code) && !l.isInformational)
            .map(tncDataMapper);
        const acceptedTncData = [...legalsSettingsNull, ...legalsSettingsNotNullAccepted];

        if (acceptedTncData.length) {
            await acceptTnc(acceptedTncData);
        }

        const declinedTncData = legalsSettingsNotNull
            .filter((l) => !acceptedSettingList.includes(l.code) && !l.isInformational)
            .map(tncDataMapper);

        if (declinedTncData.length) {
            await declineTnc(declinedTncData);
        }

        const settings = await iccMarketService.getConsumerAggregatedSettings();

        if (settings.allNonMandatoryTermsReviewed) {
            onApproved();
        }
    } catch (e) {
        appErrorService.showGlobalError();
    }
};

const setTnc = async (legals) => {
    const acceptedTncData = legals.filter((l) => !l.isInformational).map(tncDataMapper);

    await acceptTnc(acceptedTncData);

    const settings = await iccMarketService.getConsumerAggregatedSettings();

    if (settings.allMandatoryTermsApproved) {
        if (settings.allNonMandatoryTermsReviewed) {
            appRouterService.forwardToMyDevicesPage();
        } else {
            appRouterService.forwardToTNCDataCollectionPage();
        }
    }
};

const setDataCollectionTnc = async (legals, isAccepted) => {
    const tncData = legals.map(tncDataMapper);

    if (isAccepted) {
        await acceptTnc(tncData);
    } else {
        await declineTnc(tncData);
    }

    const settings = await iccMarketService.getConsumerAggregatedSettings();

    if (settings.allNonMandatoryTermsReviewed) {
        appRouterService.forwardToMyDevicesPage();
    }
};

const setUsageConsent = async (code, version) => {
    await acceptTncCode(code, version);
};

const setDiagnosticConsent = async (code, version) => {
    await acceptTncCode(code, version);
};

const acceptTncCode = async (code, version) => {
    await acceptTnc([{code, versionNumber: version || 0}]);
    await iccMarketService.getConsumerAggregatedSettings();
};

const isAggregatedSettingEnabled = (type) => {
    const aggregatedSettings = selectAggregatedSettings(getState());

    if (!aggregatedSettings) return false;

    const {consumerAggregatedSettings} = aggregatedSettings;

    if (consumerAggregatedSettings?.length) {
        return consumerAggregatedSettings.some((item) => item.key.toLowerCase() === type.toLowerCase() && item.value);
    }

    return false;
};

const openLegalSettings = (onError) => {
    const tncType = tncTypes.getTermsOfUseType();

    const onFinally = () => {
        const termsOfUse = makeSelectTncByType(tncType)(getState());
        const acceptedTermsOfUse = makeSelectAcceptedTncByType(tncType)(getState());
        const legals = acceptedTermsOfUse?.legals?.length ? acceptedTermsOfUse.legals : termsOfUse?.legals;
        const legalData = getTncLegalByTermsCode(legals);

        if (legalData) {
            dispatch(setTncPopupCode(legalData.code));
        }
    };

    checkNetworkAndTncAvailability(tncType, (isOnline) => {
        if (isOnline) {
            getTncData(onFinally, onError);
        } else {
            onFinally();
        }
    });
};

const checkNetworkAndTncAvailability = (tncType, proceedAfterChecking) => {
    if (networkStatusService.isOnline()) {
        proceedAfterChecking(true);
        return;
    }

    if (tncType) {
        const state = getState();
        const termsOfUse = makeSelectTncByType(tncType)(state);
        const acceptedTermsOfUse = makeSelectAcceptedTncByType(tncType)(state);

        if (acceptedTermsOfUse?.legals?.length || termsOfUse?.legals?.length) {
            proceedAfterChecking(false);
            return;
        }
    }

    dispatch(
        setNotificationInfo(getLocalizedStrings()[localizationKeys.NO_INTERNET_CONNECTION_SCENARIO_2_NOTIFICATION_TEXT])
    );
};

const getTncData = async (onTncLoaded, onError) => {
    try {
        dispatch(showLoader());

        await iccMarketService.getAcceptedTnc();
        await iccMarketService.getTnc();

        helpers.runFunction(onTncLoaded);
    } catch (e) {
        helpers.runFunction(onError);
    } finally {
        dispatch(hideLoader());
    }
};

const getIntToken = () => {
    const {intToken} = selectAggregatedSettings(getState()) || {};

    return intToken;
};

const getPreferenceCenterLegals = (legals, acceptedLegals) => {
    const legalsNotNull = legals?.filter((x) => !!x.settings) || [];
    const acceptedLegalsNotNull = acceptedLegals?.filter((x) => !!x.settings) || [];
    const legalsSettingsNotNull = [...legalsNotNull, ...acceptedLegalsNotNull].sort((a, b) => a.position - b.position);
    const filteredAcceptedLegals = acceptedLegalsNotNull?.filter((x) => !x.isRevoked).map(({code}) => code) || [];

    //check all aggregated settings for default value "true"
    legalsNotNull.forEach((legalNotNull) => {
        const settingsList = legalNotNull.settings.split(',');

        if (settingsList.every(isAggregatedSettingEnabled)) {
            filteredAcceptedLegals.push(legalNotNull.code);
        }
    });

    return {
        legalsSettingsNotNull,
        filteredAcceptedLegals,
    };
};

export default {
    acceptTnc,
    getIntToken,
    getParsedDescription,
    getTncCodeFromTarget,
    getTncData,
    getTncLegalByTermsCode,
    isAggregatedSettingEnabled,
    openLegalSettings,
    setDataCollectionTnc,
    setPreferenceCenterSettings,
    setTnc,
    setUsageConsent,
    setDiagnosticConsent,
    checkNetworkAndTncAvailability,
    getPreferenceCenterLegals,
};
